import { __assign, __read } from 'tslib';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import assert from 'assert';
import { useRef, Children, cloneElement, useState, useCallback, useEffect } from 'react';
import flattenChildren from 'react-keyed-flatten-children';
import { Box } from '../Box/index.js';
import { Inline } from '../Inline/Inline.js';
import { useTabsContext } from './contexts/TabsContext.js';
import { TabContextProvider } from './contexts/TabContext.js';
import { TabListProvider } from './contexts/TabsListContext.js';
import { TabsUnderline } from './TabsUnderline.js';
import { TabListContainer } from './TabListContainer.js';

var TabsList = function (_a) {
    var children = _a.children;
    var tabsContext = useTabsContext();
    var tabsRefs = useRef([]);
    var tabListContainerRef = useRef(null);
    if (!tabsContext) {
        throw new Error("Tabs rendered outside TabsProvider");
    }
    var tabItems = [];
    var tabArray = flattenChildren(children);
    var label = tabsContext.label, _b = tabsContext.size, size = _b === void 0 ? "md" : _b, look = tabsContext.look, divider = tabsContext.divider;
    var tabs = Children.map(tabArray, function (tab, index) {
        var _a;
        assert(
        // @ts-expect-error "using dom render properties"
        typeof tab === "object" && tab.type.displayName === "Tab", "Only Tab elements can be direct children of a TabsList");
        tabItems.push((_a = tab.props.item) !== null && _a !== void 0 ? _a : index);
        return (jsx(TabContextProvider, __assign({ value: { currentTabIndex: index } }, { children: cloneElement(tab, {
                tabListIndex: index,
                ref: function (ref) { return (tabsRefs.current[index] = ref); },
            }) })));
    });
    var _c = __read(useState(true), 2), showMask = _c[0], setShowMask = _c[1];
    var updateMask = useCallback(function () {
        if (!tabListContainerRef.current) {
            return;
        }
        setShowMask(tabListContainerRef.current.scrollWidth -
            tabListContainerRef.current.offsetWidth -
            tabListContainerRef.current.scrollLeft >
            10);
    }, [tabListContainerRef, setShowMask]);
    useEffect(function () {
        updateMask();
        window.addEventListener("resize", updateMask);
        return function () { return window.removeEventListener("resize", updateMask); };
    }, [updateMask]);
    useEffect(function () {
        tabsContext.tabs = tabItems;
    }, []);
    return (jsx(TabListProvider, __assign({ value: { tabsRefs: tabsRefs, tabListContainer: tabListContainerRef.current } }, { children: jsx(TabListContainer, __assign({ ref: tabListContainerRef, look: look, showMask: showMask, divider: look === "card" ? true : divider, onScroll: updateMask }, { children: jsxs(Box, __assign({ role: "tablist", "aria-label": label, themeKey: "tabList" }, { size: size }, { children: [jsx(Inline, __assign({ gap: "2", noWrap: true }, { children: tabs })), jsx(TabsUnderline, {})] })) })) })));
};

export { TabsList };
//# sourceMappingURL=TabsList.js.map
